import { useCallback, useEffect, useRef, useState } from 'react';
import { CloudUpload, ContentCopy, Refresh } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { useSnackbar } from 'notistack';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

interface RawUploadGroup {
  upload_id: string;
  prefix: string;
  file_count: number;
  total_size_bytes: number;
  label: string;
}

interface UploadFile {
  file: File;
  path: string;
}

interface OrganizationDataImportV2SectionProps {
  organizationId: string;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

const isFileEntry = (e: FileSystemEntry): e is FileSystemFileEntry => e.isFile;
const isDirectoryEntry = (e: FileSystemEntry): e is FileSystemDirectoryEntry =>
  e.isDirectory;

/** Read every entry of a directory (readEntries returns at most ~100/call). */
function readAllEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const acc: FileSystemEntry[] = [];
    const readBatch = (): void => {
      reader.readEntries(batch => {
        if (batch.length === 0) {
          resolve(acc);
        } else {
          acc.push(...batch);
          readBatch();
        }
      }, reject);
    };
    readBatch();
  });
}

/** Recursively flatten a dropped file/folder entry into files + relative paths. */
async function entryToFiles(
  entry: FileSystemEntry,
  prefix: string,
): Promise<UploadFile[]> {
  if (isFileEntry(entry)) {
    const file = await new Promise<File>((resolve, reject) =>
      entry.file(resolve, reject),
    );
    return [{ file, path: `${prefix}${file.name}` }];
  }
  if (isDirectoryEntry(entry)) {
    const entries = await readAllEntries(entry.createReader());
    const groups = await Promise.all(
      entries.map(e => entryToFiles(e, `${prefix}${entry.name}/`)),
    );
    return groups.flat();
  }
  return [];
}

/**
 * Raw source-file import for the migration agent. Drag & drop files or a whole
 * folder (or browse) — everything is staged straight into the S3 landing zone
 * (v2 raw prefix) with NO import session and NO legacy processing workflow.
 * Folder structure is preserved; the table lists what's already staged.
 */
export function OrganizationDataImportV2Section({
  organizationId,
}: OrganizationDataImportV2SectionProps) {
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploads, setUploads] = useState<RawUploadGroup[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  // `webkitdirectory` isn't a typed React attribute — set it imperatively.
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
    }
  }, []);

  const fetchUploads = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const token = await getAuth0AccessTokenSilently();
      const res = await fetch(
        `${API_BASE_URL}/v2/admin/data-migration/organizations/${organizationId}/raw-uploads`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Failed to load uploads (${res.status})`);
      const data = (await res.json()) as { uploads: RawUploadGroup[] };
      setUploads(data.uploads ?? []);
    } catch (err) {
      enqueueSnackbar(
        err instanceof Error ? err.message : 'Failed to load uploads',
        { variant: 'error' },
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, enqueueSnackbar]);

  useEffect(() => {
    void fetchUploads();
  }, [fetchUploads]);

  const uploadItems = useCallback(
    async (items: UploadFile[]) => {
      if (items.length === 0 || !organizationId) return;
      setUploading(true);
      try {
        const token = await getAuth0AccessTokenSilently();
        const form = new FormData();
        form.append('organization_id', organizationId);
        for (const item of items) {
          form.append('file', item.file, item.path);
        }

        const res = await fetch(
          `${API_BASE_URL}/v2/admin/data-migration/raw-upload`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: form,
          },
        );

        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          throw new Error(body.error ?? `Upload failed (${res.status})`);
        }

        const data = (await res.json()) as { count: number };
        enqueueSnackbar(`Uploaded ${data.count} file(s)`, {
          variant: 'success',
        });
        void fetchUploads();
      } catch (err) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Upload failed', {
          variant: 'error',
        });
      } finally {
        setUploading(false);
      }
    },
    [organizationId, enqueueSnackbar, fetchUploads],
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const dt = e.dataTransfer;
    // Grab entries synchronously — they become invalid after the handler ends.
    const entries: FileSystemEntry[] = [];
    for (const item of Array.from(dt.items)) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
    }
    const plainFiles = Array.from(dt.files);
    void (async () => {
      let collected: UploadFile[];
      if (entries.length > 0) {
        const groups = await Promise.all(entries.map(en => entryToFiles(en, '')));
        collected = groups.flat();
      } else {
        collected = plainFiles.map(file => ({ file, path: file.name }));
      }
      await uploadItems(collected);
    })();
  };

  const onPicked = (list: FileList | null) => {
    if (!list) return;
    const collected = Array.from(list).map(file => ({
      file,
      path: file.webkitRelativePath || file.name,
    }));
    void uploadItems(collected);
  };

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text);
    enqueueSnackbar('Copied to clipboard', { variant: 'success' });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Data Import (V2)"
        subheader="Upload raw source files or a folder straight to S3 — no processing. The migration agent picks them up from the v2 raw prefix."
      />
      <CardContent>
        <input
          ref={folderInputRef}
          type="file"
          hidden
          onChange={e => {
            onPicked(e.target.files);
            e.target.value = '';
          }}
        />

        <Box
          onDragOver={e => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={e => {
            e.preventDefault();
            setDragActive(false);
          }}
          onDrop={onDrop}
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 3,
            mb: 2,
            textAlign: 'center',
            bgcolor: dragActive ? 'action.hover' : 'transparent',
          }}
        >
          <CloudUpload color="action" />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Drag &amp; drop files or a folder here
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={
                uploading ? <CircularProgress size={16} /> : <CloudUpload />
              }
              disabled={uploading || !organizationId}
              onClick={() => folderInputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : 'Import Data V2'}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : uploads.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No raw uploads yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Upload</TableCell>
                <TableCell align="right">Files</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>S3 prefix</TableCell>
                <TableCell align="right">
                  <Tooltip title="Refresh">
                    <IconButton
                      size="small"
                      aria-label="Refresh raw uploads"
                      onClick={() => void fetchUploads()}
                    >
                      <Refresh fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploads.map(u => (
                <TableRow key={u.upload_id}>
                  <TableCell>{u.label}</TableCell>
                  <TableCell align="right">{u.file_count}</TableCell>
                  <TableCell align="right">
                    {formatBytes(u.total_size_bytes)}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      wordBreak: 'break-all',
                    }}
                  >
                    {u.prefix}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Copy S3 prefix">
                      <IconButton
                        size="small"
                        aria-label="Copy S3 prefix"
                        onClick={() => copyToClipboard(u.prefix)}
                      >
                        <ContentCopy fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
